if (env.isDev) {
  import("react-scan").then(({ scan }) => {
    scan({ enabled: false, showToolbar: env.isDev });
  });
}

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./Routes.tsx";
import { BrowserRouter } from "react-router";
import { Toaster } from "sonner";
import { ErrorBoundary } from "@sentry/react";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ServerHealthWrapper } from "@/components/server-health-wrapper";
import { isNetworkError, NETWORK_ERROR_RETRY_COUNT, GENERAL_ERROR_RETRY_COUNT } from "@/lib/network-utils";
import { env } from "@env";

import "@/lib/sentry.ts";

// trigger ci

// Global error handler for network/server errors
const handleQueryError = (error: Error) => {
  if (isNetworkError(error)) {
    console.log("Network error detected, will be handled by server health monitor");
    // The server health monitor will detect this and show the overlay
    // We trigger a custom event that the useServerHealth hook can listen to
    window.dispatchEvent(new CustomEvent("api-network-error", { detail: { error } }));
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      // staleTime: 1000 * 30, // 30 secondes de temps avant que les données ne deviennent obsolètes
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (isNetworkError(error)) {
          return failureCount < NETWORK_ERROR_RETRY_COUNT;
        }
        return failureCount < GENERAL_ERROR_RETRY_COUNT;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isNetworkError(error)) {
          return failureCount < NETWORK_ERROR_RETRY_COUNT;
        }
        return failureCount < GENERAL_ERROR_RETRY_COUNT;
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary
          fallback={({ error, resetError }) => (
            <div className="rounded-md bg-red-100 p-4 text-red-800">
              <p>
                Une erreur est survenue : {typeof error === "object" && error && "message" in error ? (error as { message?: string }).message : String(error)}
              </p>
              <button onClick={resetError} className="mt-2 text-blue-600 hover:underline">
                Réessayer
              </button>
            </div>
          )}
        >
          <ServerHealthWrapper>
            <App />
          </ServerHealthWrapper>
        </ErrorBoundary>
        <Toaster position="bottom-left" toastOptions={{ duration: 2000 }} />
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>
);
